/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import TicketList from "./components/TicketList";
import TicketDetailsAccordion from "./components/TicketDetailsAccordion";
import TicketOfferDetailsAccordion from "./components/TicketOfferDetailsAccordion";
import TicketChatBox from './components/TicketChatBox';
import SweetAlert2 from "react-sweetalert2";
import { usePageWebSocket } from '../../../hooks/usePageWebSocket';

const TICKET_PER_PAGE = process.env.REACT_APP_TICKET_DASHBOARD_PER_PAGE || 10;

interface PayloadType {
  [key: string]: any;
}

const TradingDesk = () => {
  const [isFetchingList, setIsFetchingList] = useState<any>(false);
  const [ticketList, setTicketList] = useState([]);
  const [ticketClosedList, setTicketClosedList] = useState([]);
  const [tradeDetail, setTradeDetail] = useState(null);
  const [offerDetail, setOfferDetail] = useState(null);
  const [swalProps, setSwalProps] = useState({});
  const [activeToNewTicketId, setActiveToNewTicketId] = useState<string>("");
  const [openTicketsLastKey, setOpenTicketsLastKey] = useState<string>("");
  const [hasMoreOpenTickets, setHasMoreOpenTickets] = useState<boolean>(false);
  const [closedTicketsLastKey, setClosedTicketsLastKey] = useState<string>("");
  const [hasMoreClosedTickets, setHasMoreClosedTickets] = useState<boolean>(false);
  const [isLoadingMoreOpen, setIsLoadingMoreOpen] = useState<boolean>(false);
  const [isLoadingMoreClosed, setIsLoadingMoreClosed] = useState<boolean>(false);
  const [isChatLoading, setIsChatLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<"open" | "closed">("open");
  const [openTicketCount, setOpenTicketCount] = useState<number>(0);
  const userid = localStorage.getItem("userid");
  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const activeToNewTicketIdFromStorage = localStorage.getItem("activeToNewTicketIdFromStorage");
  const subaccount_id = localStorage.getItem("subaccountId");
  const { received } = usePageWebSocket(userRole === "superadmin" ? "desk" : "desk_" + subaccount_id);

  // AbortController for cancelling requests on unmount
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  // Cleanup: Cancel all pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Create new AbortController for each batch of requests
  const getAbortSignal = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };

  useEffect(() => {
    const handleNewMessage = async () => {
      if (!received) return;

      if (received.messageType === 'new-trade') {
        if (userRole === "superadmin") {
          await fetchTicketListForSuperadmin(false);
          await fetchClosedTicketListForSuperadmin(false);
        } else {
          await fetchTicketList(false, received?.data?.trade_id);
          await fetchClosedTicketList(false);
        }
      } else if (received.messageType === 'close-ticket') {
        const closedTicketId = received?.data?.ticket_id || received?.data?.trade_id;

        // 1. Remove ticket from open list
        setTicketList(prev => {
          const filtered = prev.filter(ticket => ticket.id !== closedTicketId);
          return filtered;
        });

        // 2. Update open ticket count
        setOpenTicketCount(prev => Math.max(0, prev - 1));

        // 3. Handle if user is currently viewing this ticket
        if (tradeDetail && tradeDetail.id === closedTicketId) {
          setTradeDetail((prev: any) => ({
            ...prev,
            conversation_status: 'closed'
          }));
        }

        if (userRole === "superadmin") {
          await fetchClosedTicketListForSuperadmin(false);
        } else {
          await fetchClosedTicketList(false);
        }

        // 4. Backfill open list if we're on the open tab and have space
        if (activeTab === 'open') {
          // Fetch fresh open list to backfill the gap
          if (userRole === "superadmin") {
            await fetchTicketListForSuperadmin(false);
          } else {
            await fetchTicketList(false);
          }
        }
      }
    };

    handleNewMessage();
  }, [received]); // eslint-disable-line

  useEffect(() => {
    const signal = getAbortSignal();
    const currentRequestId = ++requestIdRef.current;

    const fetchData = async () => {
      setIsFetchingList(true);
      if (userRole === "superadmin") {
        await fetchTicketListForSuperadmin(false, signal);
        if (signal.aborted || requestIdRef.current !== currentRequestId) return;
        await fetchClosedTicketListForSuperadmin(false, signal);
        if (signal.aborted || requestIdRef.current !== currentRequestId) return;
      } else {
        await fetchTicketList(false, activeToNewTicketIdFromStorage, signal);
        if (signal.aborted || requestIdRef.current !== currentRequestId) return;
        await fetchClosedTicketList(false, signal);
        if (signal.aborted || requestIdRef.current !== currentRequestId) return;
      }
      setIsFetchingList(false);
    };

    fetchData();
  }, []); // eslint-disable-line

  const fetchTicketListForSuperadmin = async (append = false, signal?: AbortSignal) => {
    try {
      const queryObject: PayloadType = {
        limit: TICKET_PER_PAGE,
        conversation_status: "open",
        ...(append && hasMoreOpenTickets && { lastkey: openTicketsLastKey })
      };

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      let requestUrl = `/api/trade/trades-for-ticket?${queryString}`;
      const response = await axios.get(process.env.REACT_APP_API_URL + requestUrl, { signal });

      const trades = response.data.trades.payload || [];
      const openTicket = response.data.trades.totalOpenConversationTrade || 0;
      // setTradeDetail(trades[0]);
      // setActiveToNewTicketId(trades[0].id);
      setHasMoreOpenTickets(response.data.trades.lastkey ? true : false);
      setOpenTicketsLastKey(response.data.trades.lastkey ? response.data.trades.lastkey : "");
      if (append) {
        setTicketList(prev => [...prev, ...trades]);
      } else {
        setTicketList(trades);
      }
      setOpenTicketCount(openTicket);
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error("Error fetching trades", error);
    }
  }

  const fetchTicketList = async (append = false, newTradeComingInId: string = "", signal?: AbortSignal) => {
    try {
      const queryObject: PayloadType = {
        limit: TICKET_PER_PAGE,
        conversation_status: "open",
        ...(append && hasMoreOpenTickets && { lastkey: openTicketsLastKey })
      };

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      let requestUrl = `/api/trade/trades-for-ticket?${queryString}`;
      const response = await axios.get(process.env.REACT_APP_API_URL + requestUrl, { signal });

      const trades = response.data.trades.payload || [];
      const openTicket = response.data.trades.totalOpenConversationTrade || 0;
      setHasMoreOpenTickets(response.data.trades.lastkey ? true : false);
      setOpenTicketsLastKey(response.data.trades.lastkey ? response.data.trades.lastkey : "");
      if (append) {
        setTicketList(prev => [...prev, ...trades]);
      } else {
        setTicketList(trades);
      }
      setOpenTicketCount(openTicket);

      if (newTradeComingInId && newTradeComingInId !== "") {
        setActiveTab("open");
        setActiveToNewTicketId(newTradeComingInId);
      }
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error("Error fetching trades", error);
    }
  }

  const handleLoadMoreOpen = async () => {
    if (isLoadingMoreOpen) return;

    setIsLoadingMoreOpen(true);

    try {
      if (userRole === "superadmin") {
        await fetchTicketListForSuperadmin(true);
      } else {
        await fetchTicketList(true);
      }
    } catch (error) {
      console.error("Error loading more open tickets:", error);
    } finally {
      setIsLoadingMoreOpen(false);
    }
  };

  const fetchClosedTicketListForSuperadmin = async (append = false, signal?: AbortSignal) => {
    try {
      const queryObject: PayloadType = {
        limit: TICKET_PER_PAGE,
        conversation_status: "closed",
        ...(hasMoreClosedTickets && { lastkey: closedTicketsLastKey })
      };

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      let requestUrl = `/api/trade/trades-for-ticket?${queryString}`;
      const response = await axios.get(process.env.REACT_APP_API_URL + requestUrl, { signal });

      const trades = response.data.trades.payload || [];
      setHasMoreClosedTickets(response.data.trades.lastkey ? true : false);
      setClosedTicketsLastKey(response.data.trades.lastkey ? response.data.trades.lastkey : "");

      if (append) {
        setTicketClosedList(prev => [...prev, ...trades]);
      } else {
        setTicketClosedList(trades);
      }
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error("Error fetching trades", error);
    }
  }

  const fetchClosedTicketList = async (append = false, signal?: AbortSignal) => {
    try {
      const queryObject: PayloadType = {
        limit: TICKET_PER_PAGE,
        conversation_status: "closed",
        ...(hasMoreClosedTickets && { lastkey: closedTicketsLastKey })
      };

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      let requestUrl = `/api/trade/trades-for-ticket?${queryString}`;
      const response = await axios.get(process.env.REACT_APP_API_URL + requestUrl, { signal });

      const trades = response.data.trades.payload || [];
      setHasMoreClosedTickets(response.data.trades.lastkey ? true : false);
      setClosedTicketsLastKey(response.data.trades.lastkey ? response.data.trades.lastkey : "");

      if (append) {
        setTicketClosedList(prev => [...prev, ...trades]);
      } else {
        setTicketClosedList(trades);
      }
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error("Error fetching trades", error);
    }
  }

  const handleLoadMoreClosed = async () => {
    if (isLoadingMoreClosed) return;

    setIsLoadingMoreClosed(true);

    try {
      if (userRole === "superadmin") {
        await fetchClosedTicketListForSuperadmin(true);
      } else {
        await fetchClosedTicketList(true);
      }
    } catch (error) {
      console.error("Error loading more closed tickets:", error);
    } finally {
      setIsLoadingMoreClosed(false);
    }
  };

  // Handle tab switching in parent
  const handleTabSwitch = (tab: "open" | "closed") => {
    setActiveTab(tab);
    setTradeDetail(null);
    setOfferDetail(null);
  };

  return (
    <>
      <div className="card flex h-[calc(100vh-130px)] flex-col !rounded-xl md:flex-row">
        <aside className="w-full border-b border-gray-200 py-4 md:w-[360px] md:border-b-0 md:border-r flex flex-col">
          <h2 className="mb-5 px-4 text-xl font-bold text-navy-700">Trading Desk</h2>

          {/* Fixed header - tabs and search */}
          <div className="px-3 flex-shrink-0 mb-4">
            <div className="mb-4 flex w-full max-w-[360px] items-center justify-between rounded-full bg-[#F1F5FB] p-1 text-sm font-medium">
              <button
                onClick={() => handleTabSwitch("open")}
                className={`flex-1 rounded-full px-3 py-1 text-center text-xs transition-all duration-200 ${activeTab === "open"
                  ? "bg-white text-navy-700 shadow"
                  : "text-gray-400 hover:text-gray-600"
                  }`}
              >
                Open{" "}
                {openTicketCount > 0 && (
                  <span className="ml-1 rounded-full bg-primary-500 px-1.5 py-0.5 text-[12px] text-white">
                    {openTicketCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => handleTabSwitch("closed")}
                className={`flex-1 px-3 py-1 text-xs transition-all duration-200 ${activeTab === "closed"
                  ? "bg-white text-navy-700 shadow rounded-full"
                  : "text-gray-400 hover:text-gray-600"
                  }`}
              >
                Closed
              </button>
            </div>
            {/* <div className="relative">
              <input
                type="text"
                placeholder="Search"
                className="focus:border-primary-500 w-full rounded-full bg-white px-4 py-2 pl-10 text-sm placeholder-gray-400 shadow-md focus:outline-none"
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="absolute left-3 top-2.5 h-4 w-4 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div> */}
          </div>

          {/* Scrollable ticket list area */}
          <div className="flex-1 overflow-y-auto">
            <TicketList
              ticketList={ticketList}
              ticketClosedList={ticketClosedList}
              activeTab={activeTab}
              activeToNewTicketId={activeToNewTicketId}
              setActiveToNewTicketId={setActiveToNewTicketId}
              setTradeDetail={setTradeDetail}
              setOfferDetail={setOfferDetail}
              isFetchingList={isFetchingList}
              hasMoreOpenTickets={hasMoreOpenTickets}
              hasMoreClosedTickets={hasMoreClosedTickets}
              isLoadingMoreOpen={isLoadingMoreOpen}
              isLoadingMoreClosed={isLoadingMoreClosed}
              setIsChatLoading={setIsChatLoading}
              onLoadMoreOpen={handleLoadMoreOpen}
              onLoadMoreClosed={handleLoadMoreClosed}
            />
          </div>
        </aside>
        {tradeDetail &&
          <TicketChatBox
            tradeDetail={tradeDetail}
            setTradeDetail={setTradeDetail}
            setSwalProps={setSwalProps}
            latestOfferDetail={offerDetail}
            setLatestOfferDetail={setOfferDetail}
            isChatLoading={isChatLoading}
            setIsChatLoading={setIsChatLoading}
          />
        }
        <aside className="w-full gap-4 border-t border-gray-200 px-4 pb-6 pt-6 md:w-[360px] md:border-l md:border-t-0">
          {tradeDetail &&
            <>
              <TicketDetailsAccordion
                tradeDetail={tradeDetail}
              />
              {offerDetail &&
                <TicketOfferDetailsAccordion
                  tradeDetail={tradeDetail}
                  offerDetail={offerDetail}
                  setSwalProps={setSwalProps}
                />
              }
            </>
          }
        </aside>
      </div>
      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </>
  );
};

export default TradingDesk;